import { Link } from "@tanstack/react-router";
import { Bot } from "lucide-react";
import type { ReactNode } from "react";
import { fmtDateTime, fmtRelative } from "@/lib/format";
import { Tooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { SEVERITY_COLOR } from "./status";
import { formatImpact, IMPACT_KIND_LABEL, type Impact, type Severity } from "@ti/core";

export function TimeAgo({ iso, className }: { iso: string; className?: string }) {
  return (
    <Tooltip content={fmtDateTime(iso)}>
      <time dateTime={iso} className={cn("whitespace-nowrap text-fg-2 tabular", className)}>
        {fmtRelative(iso)}
      </time>
    </Tooltip>
  );
}

export function AgentLink({ id, name, className }: { id: string; name: string; className?: string }) {
  return (
    <Link to="/agents/$agentId" params={{ agentId: id }} onClick={(e) => e.stopPropagation()} className={cn("inline-flex items-center gap-1 font-mono text-[12.5px] text-fg hover:text-accent hover:underline", className)}>
      <Bot className="size-3.5 text-fg-3" />
      {name}
    </Link>
  );
}

export function SignalChip({ type, severity, maxWidth }: { type: string; severity: Severity; maxWidth?: number }) {
  return (
    <span title={type} className="inline-flex min-w-0 items-center gap-1 rounded border bg-surface-2 px-1.5 py-px font-mono text-[11px] text-fg-2" style={maxWidth ? { maxWidth } : undefined}>
      <span className="size-1.5 shrink-0 rounded-full" style={{ background: SEVERITY_COLOR[severity] }} aria-label={`${severity} severity`} />
      <span className="truncate">{type}</span>
    </span>
  );
}

export function PageHeader({ title, description, actions, breadcrumb, className }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; breadcrumb?: ReactNode; className?: string }) {
  return (
    <div className={cn("flex flex-wrap items-end justify-between gap-3 pb-5", className)}>
      <div className="min-w-0">
        {breadcrumb ? <div className="mb-1.5 text-xs text-fg-3">{breadcrumb}</div> : null}
        <h1 className="text-lg font-semibold tracking-tight">{title}</h1>
        {description ? <p className="mt-1 max-w-3xl text-[13px] text-fg-3">{description}</p> : null}
      </div>
      {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function SectionTitle({ children, actions }: { children: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-2 flex items-center justify-between">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-fg-3">{children}</h2>
      {actions}
    </div>
  );
}

export function KeyValue({ items, className }: { items: Array<[ReactNode, ReactNode]>; className?: string }) {
  return (
    <dl className={cn("grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-[13px]", className)}>
      {items.map(([k, v], i) => (
        <div key={i} className="contents">
          <dt className="text-fg-3">{k}</dt>
          <dd className="min-w-0 text-fg">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Impact of an action (or a list of impacts), formatted in its own units: "$420", "4,212 rows", "3 pods". */
export function ImpactList({ impact, className, compact, max = 3 }: { impact: Impact | Impact[] | null | undefined; className?: string; compact?: boolean; max?: number }) {
  const list = (Array.isArray(impact) ? impact : impact ? [impact] : []).filter((i) => i.amount !== 0);
  if (!list.length) return <span className={cn("text-fg-3", className)}>—</span>;
  const shown = list.slice(0, max);
  const rest = list.length - shown.length;
  return (
    <span className={cn("inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5 whitespace-nowrap tabular", className)}>
      {shown.map((i, n) => (
        <span key={`${i.kind}:${i.unit}`} title={IMPACT_KIND_LABEL[i.kind]} className={n > 0 ? "text-fg-2" : undefined}>
          {n > 0 ? <span className="mr-1.5 text-fg-3" aria-hidden>·</span> : null}
          {formatImpact(i, { compact })}
        </span>
      ))}
      {rest > 0 ? <span className="text-fg-3">+{rest}</span> : null}
    </span>
  );
}
